import { Active } from '@lumapps/lumx-dnd/types';

import { SortableListProps } from '../components/SortableList/types';

export interface SortableListContainerProps<T>
    extends Pick<SortableListProps<T>, 'columns' | 'gap'>,
        Pick<Required<SortableListProps<T>>, 'mode'> {
    allItems: HTMLLIElement[];
    event: React.KeyboardEvent;
    active: Active | null;
}

export const getNewSortableListIndexFromKeyboardEvent = ({
    active,
    mode,
    columns,
    allItems,
    event,
}: SortableListContainerProps<any>): number | undefined => {
    // Only handle keyboard navigation in grid mode
    if (mode !== 'grid' || !columns || !allItems) {
        return undefined;
    }

    // If there's an active drag operation, don't handle navigation
    // The dnd-kit KeyboardSensor will handle the dragging movement
    if (active) {
        return undefined;
    }

    const target = event.target as HTMLElement;
    const currentIndex = allItems.findIndex((item) => item.contains(target));

    if (currentIndex === -1) {
        return undefined;
    }

    const totalItems = allItems.length;
    const cols = columns;
    const currentRow = Math.floor(currentIndex / cols);
    const currentCol = currentIndex % cols;

    let newIndex = currentIndex;

    switch (event.key) {
        case 'ArrowRight':
            newIndex = currentIndex + 1;
            if (newIndex >= totalItems) {
                newIndex = totalItems - 1;
            }
            break;

        case 'ArrowLeft':
            newIndex = currentIndex - 1;
            if (newIndex < 0) {
                newIndex = 0;
            }
            break;

        case 'ArrowDown':
            newIndex = currentIndex + cols;
            if (newIndex >= totalItems) {
                // Go to the last item
                newIndex = totalItems - 1;
            }
            break;

        case 'ArrowUp':
            newIndex = currentIndex - cols;
            if (newIndex < 0) {
                // Go to the first item in the same column
                newIndex = currentCol;
            }
            break;

        case 'Home':
            if (event.ctrlKey) {
                // Ctrl+Home: Go to first item in grid
                newIndex = 0;
            } else {
                // Home: Go to start of current row
                newIndex = currentRow * cols;
            }
            break;

        case 'End':
            if (event.ctrlKey) {
                // Ctrl+End: Go to last item in grid
                newIndex = totalItems - 1;
            } else {
                // End: Go to end of current row
                const rowStart = currentRow * cols;
                const rowEnd = Math.min(rowStart + cols - 1, totalItems - 1);
                newIndex = rowEnd;
            }
            break;

        case 'PageUp':
            // PageUp: Go to first item in current column
            newIndex = currentCol;
            break;

        case 'PageDown': {
            // PageDown: Go to last item in current column
            const lastRowForCol = Math.floor((totalItems - 1) / cols);
            if (currentCol <= (totalItems - 1) % cols) {
                newIndex = lastRowForCol * cols + currentCol;
            } else {
                newIndex = (lastRowForCol - 1) * cols + currentCol;
            }
            break;
        }

        default:
            return undefined;
    }

    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < totalItems) {
        return newIndex;
    }

    return undefined;
};
